import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, ClientProfile, UserRole } from '@ftt/shared';
import { api } from './api';

interface AuthContextType {
  user: User | null;
  clientProfile: ClientProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isClient: boolean;
  login: (token: string, user: User, clientProfile?: ClientProfile | null) => void;
  logout: () => void;
  updateUser: (user: User, clientProfile?: ClientProfile | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('ftt_token');
    const savedUser = localStorage.getItem('ftt_user');
    const savedProfile = localStorage.getItem('ftt_profile');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        if (savedProfile) {
          setClientProfile(JSON.parse(savedProfile));
        }

        // Verify with server
        api.get('/auth/me')
          .then((res) => {
            if (res.data?.success) {
              setUser(res.data.data.user);
              setClientProfile(res.data.data.clientProfile);
              localStorage.setItem('ftt_user', JSON.stringify(res.data.data.user));
              if (res.data.data.clientProfile) {
                localStorage.setItem('ftt_profile', JSON.stringify(res.data.data.clientProfile));
              }
            }
          })
          .catch(() => {
            // Token might be invalid
          })
          .finally(() => {
            setIsLoading(false);
          });
      } catch (err) {
        localStorage.removeItem('ftt_token');
        localStorage.removeItem('ftt_user');
        localStorage.removeItem('ftt_profile');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = (newToken: string, newUser: User, newProfile?: ClientProfile | null) => {
    setToken(newToken);
    setUser(newUser);
    setClientProfile(newProfile || null);
    localStorage.setItem('ftt_token', newToken);
    localStorage.setItem('ftt_user', JSON.stringify(newUser));
    if (newProfile) {
      localStorage.setItem('ftt_profile', JSON.stringify(newProfile));
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setClientProfile(null);
    localStorage.removeItem('ftt_token');
    localStorage.removeItem('ftt_user');
    localStorage.removeItem('ftt_profile');
    window.location.href = '/login';
  };

  const updateUser = (updatedUser: User, updatedProfile?: ClientProfile | null) => {
    setUser(updatedUser);
    localStorage.setItem('ftt_user', JSON.stringify(updatedUser));
    if (updatedProfile !== undefined) {
      setClientProfile(updatedProfile);
      if (updatedProfile) {
        localStorage.setItem('ftt_profile', JSON.stringify(updatedProfile));
      } else {
        localStorage.removeItem('ftt_profile');
      }
    }
  };

  const role: UserRole | undefined = user?.role;
  const isAdmin = role === 'SUPER_ADMIN' || role === 'ADMIN';
  const isStaff = isAdmin || role === 'EDITOR';
  const isClient = role === 'CLIENT';

  return (
    <AuthContext.Provider
      value={{
        user,
        clientProfile,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        isAdmin,
        isStaff,
        isClient,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
